/* istanbul ignore file */ /* plugin extension code */
/* eslint-disable functional/immutable-data */

import React, { useCallback, useEffect, useState } from 'react';
import { logger as LOG, useBreakpointHelpers } from '@sweetgreen/garnish';

import {
  getKustomerKeys,
  useKustomerAvailability,
} from './kustomer.availability';
import { useKustomerToken } from './kustomer.hooks';
import { useKustomerNavigation } from './kustomer.navigation';
import type { KustomerCallbackResponse, KustomerWindow } from './types';

// ────────────────────────────────────────────────────────────────────────────────
// LOG.enable('KUSTOMER');
const logger = LOG.extend('KUSTOMER');
const KustomerObject = () => (window as unknown as KustomerWindow).Kustomer;

export const useKustomer = () => {
  const isKustomerEnabled = useKustomerAvailability();
  const { isMobile } = useBreakpointHelpers();
  const { brandId } = getKustomerKeys();
  const { kustomerToken, fetchKustomerToken } =
    useKustomerToken(isKustomerEnabled);
  const [openingKustomer, setOpeningKustomer] = React.useState(false);

  const startKustomer = useCallback(() => {
    if (!isKustomerEnabled) return;
    logger.info('Starting');
    KustomerObject()?.start?.({ brandId }, () => {
      fetchKustomerToken();
    });
  }, [isKustomerEnabled, brandId, fetchKustomerToken]);

  const openKustomer = useCallback(() => {
    if (!isKustomerEnabled) return;
    logger.info('Opening');
    setOpeningKustomer(true);

    const isKustomerReady =
      typeof KustomerObject()?.open === 'function' &&
      typeof KustomerObject()?.addListener === 'function';

    if (isKustomerReady) {
      KustomerObject()?.open?.();
      KustomerObject()?.addListener?.('onOpen', () => {
        setOpeningKustomer(false);
      });

      if (isMobile) {
        KustomerObject()?.addListener('onClose', removeKustomerWidget);
      }
    }

    KustomerObject()?.start?.({ brandId }, () => {
      fetchKustomerToken();
      KustomerObject()?.open?.();
      KustomerObject()?.addListener?.('onOpen', () => {
        setOpeningKustomer(false);
      });

      if (isMobile) {
        KustomerObject()?.addListener('onClose', removeKustomerWidget);
      }
    });
  }, [isKustomerEnabled, brandId, isMobile, fetchKustomerToken]);

  const loadAndStartKustomer = useKustomerScript(startKustomer);
  const loadAndOpenKustomer = useKustomerScript(openKustomer);

  useKustomerLogin(kustomerToken);

  return {
    isKustomerEnabled,
    openingKustomer,
    start: loadAndStartKustomer,
    open: loadAndOpenKustomer,
  };
};

export const useLogoutKustomer = () => {
  return useCallback(() => {
    KustomerObject()?.logout?.();
  }, []);
};

export const useRemoveKustomerWidget = () => {
  const isKustomerEnabled = useKustomerAvailability();
  const { canShowKustomerWidget } = useKustomerNavigation();
  const { isMobile } = useBreakpointHelpers();
  const shouldRemove = isMobile || !canShowKustomerWidget || !isKustomerEnabled;

  React.useEffect(() => {
    if (!shouldRemove) return;
    removeKustomerWidget();
  }, [shouldRemove]);
};

// Authentication with Kustomer SDK.
const useKustomerLogin = (kustomerToken?: string) => {
  const [isLoggedInKustomer, setIsLoggedInKustomer] = useState(false);
  const shouldLogin = kustomerToken && !isLoggedInKustomer;

  useEffect(() => {
    if (!shouldLogin) return;
    logger.info('Logging-in');

    KustomerObject()?.login(
      {
        jwtToken: kustomerToken,
      },
      (loginCallbackResponse: KustomerCallbackResponse, error) => {
        if (error) {
          logger.info('Failed to log-in', error);
          setIsLoggedInKustomer(false);

          return;
        }

        logger.info('Log-in success', loginCallbackResponse.identified);
        setIsLoggedInKustomer(loginCallbackResponse.identified);
      },
    );
  }, [kustomerToken, isLoggedInKustomer, shouldLogin]);
};

// Adding Kustomer script to html.
const useKustomerScript = (onLoad: () => void) => {
  return useCallback(() => {
    const kustomerScript = getKustomerScript();

    if (kustomerScript) {
      onLoad();

      return;
    }

    document.body.insertBefore(
      getScript(onLoad),
      document.body.childNodes[document.body.childNodes.length - 1],
    );
  }, [onLoad]);
};

// Removing Kustomer iFrame from certain screens.
const removeKustomerWidget = () => {
  const kustomerWidget = getKustomerIframe();

  if (!kustomerWidget) return;

  logger.info('Removing Widget');
  stopObservingKustomerIframe();
  kustomerWidget.remove();
};

// Removing invalid aria tag from Kustomer iframe.

/**
 * Removes `aria-modal` attribute from the Kustomer `iframe` element
 *
 * Kustomer sets `aria-modal` a11y attribute on their `iframe` which is only valid,
 * when the element has the corresponding `role` attribute (in this case, `role="dialog"`),
 * However, because 'iframe' does not currently support that, we must remove the 'aria-modal' attribute.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-modal
 * @see https://github.com/w3c/html-aria/issues/217
 */
const removeAriaModalFromKustomerIframe = () => {
  const kustomerWidgetIframe = getKustomerIframe();

  if (!kustomerWidgetIframe) return;

  kustomerWidgetIframe.removeAttribute('aria-modal');
  stopObservingKustomerIframe();
};

const startObservingKustomerIframe = () => {
  const kustomerScript = getKustomerScript();

  kustomerScript?.iframeObserver?.observe(document.body, { childList: true });
};

const stopObservingKustomerIframe = () => {
  const kustomerScript = getKustomerScript();

  if (!kustomerScript?.iframeObserver) return;

  kustomerScript.iframeObserver?.disconnect();
  kustomerScript.iframeObserver = undefined;
};

const getKustomerScript = () => {
  const kustomerScript = document.querySelector(`#${KUSTOMER_SCRIPT_ID}`);

  if (!kustomerScript) return;

  return kustomerScript as KustomerScript;
};

const getKustomerIframe = () => {
  return document.querySelector('#kustomer-ui-sdk-iframe');
};

// Loading Kustomer widget script.
const getScript = (onLoad: () => void) => {
  const { apiKey } = getKustomerKeys();
  const script = document.createElement('script') as KustomerScript;

  script.id = KUSTOMER_SCRIPT_ID;
  script.src = 'https://cdn.kustomerapp.com/chat-web/widget.js';
  script.dataset.kustomerApiKey = apiKey;
  script.addEventListener('load', () => {
    onLoad();

    script.iframeObserver = new MutationObserver(
      removeAriaModalFromKustomerIframe,
    );
    startObservingKustomerIframe();
  });

  return script;
};

// ─── Constants ──────────────────────────────────────────────────────────────────

const KUSTOMER_SCRIPT_ID = 'kustomer-script';

// ─── Types ──────────────────────────────────────────────────────────────────────

type KustomerScript = {
  // eslint-disable-next-line functional/prefer-readonly-type
  iframeObserver?: MutationObserver;
} & HTMLScriptElement;
